import { motion } from "framer-motion";
import { FlaskConical, Filter, Microscope, ShieldCheck, Package, Truck } from "lucide-react";

const STEPS = [
  { icon: FlaskConical, label: "Manufacturing", desc: "Synthesized under controlled lab conditions." },
  { icon: Filter, label: "Purification", desc: "Multi-stage removal of synthesis byproducts." },
  { icon: Microscope, label: "Testing", desc: "HPLC & mass spectrometry verification." },
  { icon: ShieldCheck, label: "Quality Approval", desc: "Minimum 98% purity threshold enforced." },
  { icon: Package, label: "Packaging", desc: "Vacuum-sealed, tamper-evident sealing." },
  { icon: Truck, label: "Shipping", desc: "Insulated packaging preserves stability." },
];

export default function ResearchTimeline() {
  return (
    <section className="relative py-24 bg-slate-50 overflow-hidden" data-testid="research-timeline-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600">Our Process</span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 font-display">From Synthesis to Shipment</h2>
        </div>

        <div className="relative flex flex-col md:flex-row items-center md:items-start justify-between gap-10 md:gap-4">
          <div className="hidden md:block absolute top-7 left-0 right-0 h-px bg-slate-200" />
          <motion.div
            className="hidden md:block absolute top-7 left-0 h-px bg-gradient-to-r from-blue-900 to-blue-500"
            initial={{ width: "0%" }}
            whileInView={{ width: "100%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: "easeInOut" }}
          />
          {STEPS.map((step, idx) => (
            <motion.div
              key={step.label}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: idx * 0.1 }}
              className="group relative flex flex-col items-center text-center flex-1"
              data-testid={`timeline-step-${step.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="h-14 w-14 rounded-full bg-white border-2 border-brandblue-600 flex items-center justify-center relative z-10 shadow-sm group-hover:shadow-blue-glow group-hover:scale-110 group-hover:border-blue-500 transition-all duration-300">
                <step.icon className="h-6 w-6 text-brandblue-600" strokeWidth={1.5} />
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-900">{step.label}</p>
              <span className="text-xs text-slate-400 mt-1">Step {idx + 1}</span>
              <p className="mt-2 text-[11px] text-slate-500 leading-relaxed max-w-[130px] opacity-0 max-h-0 overflow-hidden group-hover:opacity-100 group-hover:max-h-16 transition-all duration-300">
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
